import { useCallback } from "react";

import { useTreeRevealRequester } from "@/components/modules/tree";

import { SidebarPaneId, useOpenSidebarPane } from "../panes/usePaneState";
import {
  DATA_SOURCES_TREE_ID,
  DRAFTS_TREE_ID,
  EXPORTS_TREE_ID,
  PUBLISHED_TREE_ID,
} from "../treeIds";

/**
 * Opens the owning pane and queues a reveal on its tree. Never activates and never writes
 * selection (docs/model-tool-sidebar-tree.md §3.1) — callers wanting both do both explicitly, so
 * the navigation stays visible at the call site.
 */
export type RevealKind =
  | "model"
  | "dashboard"
  | "modelFolder"
  | "draftModel"
  | "localDraft"
  | "rawView"
  | "export";

const ROUTING: Record<RevealKind, { paneId: SidebarPaneId; treeId: string }> = {
  model: { paneId: "published", treeId: PUBLISHED_TREE_ID },
  dashboard: { paneId: "published", treeId: PUBLISHED_TREE_ID },
  modelFolder: { paneId: "published", treeId: PUBLISHED_TREE_ID },
  draftModel: { paneId: "drafts", treeId: DRAFTS_TREE_ID },
  localDraft: { paneId: "drafts", treeId: DRAFTS_TREE_ID },
  rawView: { paneId: "data-sources", treeId: DATA_SOURCES_TREE_ID },
  export: { paneId: "exports", treeId: EXPORTS_TREE_ID },
};

export type RevealInSidebarArgs = {
  kind: RevealKind;
  id: string;
  /** Only meaningful for a folder. */
  expandTarget?: boolean;
  /** User-initiated callers only. */
  focus?: boolean;
  align?: "start" | "center" | "end" | "auto";
};

export function useRevealInSidebar() {
  const openPane = useOpenSidebarPane();

  // Hooks cannot be called dynamically, and there are only four trees.
  const requestPublished = useTreeRevealRequester(PUBLISHED_TREE_ID);
  const requestDrafts = useTreeRevealRequester(DRAFTS_TREE_ID);
  const requestDataSources = useTreeRevealRequester(DATA_SOURCES_TREE_ID);
  const requestExports = useTreeRevealRequester(EXPORTS_TREE_ID);

  return useCallback(
    ({ kind, id, ...options }: RevealInSidebarArgs) => {
      const route = ROUTING[kind];
      if (route == null) return;

      openPane(route.paneId);

      const request = { targetId: id, ...options };
      switch (route.treeId) {
        case PUBLISHED_TREE_ID:
          requestPublished(request);
          break;
        case DRAFTS_TREE_ID:
          requestDrafts(request);
          break;
        case DATA_SOURCES_TREE_ID:
          requestDataSources(request);
          break;
        case EXPORTS_TREE_ID:
          requestExports(request);
          break;
      }
    },
    [
      openPane,
      requestPublished,
      requestDrafts,
      requestDataSources,
      requestExports,
    ],
  );
}
